import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "@/utils/api";
import { ROLE_LABELS, fmt } from "@/utils/format";
import {
  LayoutDashboard, Users, Package, ShoppingCart,
  FileSpreadsheet, UserCog, Settings, LogOut,
  Menu, X, ChevronRight, BarChart3, Globe, Receipt,
  ArrowLeftRight, Percent, Link2,
} from "lucide-react";
import { NAV } from "@/constants/testIds";
import BrandLogo from "@/components/BrandLogo";
import { playOrderNotification } from "@/utils/orderSound";

const NAV_ITEMS = {
  admin: [
    { path: "/dashboard",          labelKey: "nav.dashboard",    icon: LayoutDashboard },
    { path: "/cariler",            labelKey: "nav.cari",         icon: Users },
    { path: "/stoklar",            labelKey: "nav.stok",         icon: Package },
    { path: "/arac-oem-baglantilari", label: "Araç–OEM Bağlantıları", icon: Link2 },
    { path: "/sepet",              labelKey: "nav.sepet",        icon: ShoppingCart, cart: true },
    { path: "/stok-hareketleri",  labelKey: "nav.stokHareketleri", icon: ArrowLeftRight },
    { path: "/siparisler",         labelKey: "nav.siparisler",   icon: ShoppingCart },
    { path: "/faturalar",          labelKey: "nav.faturalar",    icon: Receipt },
    { path: "/raporlar",           labelKey: "nav.raporlar",     icon: BarChart3 },
    { path: "/excel-import",       labelKey: "nav.excelImport",  icon: FileSpreadsheet },
    { path: "/marj-kontrolu",      labelKey: "nav.marjKontrolu", icon: Percent },
    { path: "/kullanici-yonetimi", labelKey: "nav.kullanicilar", icon: UserCog },
    { path: "/ayarlar",            labelKey: "nav.ayarlar",      icon: Settings },
  ],
  satis_destek: [
    { path: "/dashboard",    labelKey: "nav.dashboard",   icon: LayoutDashboard },
    { path: "/cariler",      labelKey: "nav.cari",        icon: Users },
    { path: "/stoklar",      labelKey: "nav.stok",        icon: Package },
    { path: "/arac-oem-baglantilari", label: "Araç–OEM Bağlantıları", icon: Link2 },
    { path: "/sepet",        labelKey: "nav.sepet",       icon: ShoppingCart, cart: true },
    { path: "/stok-hareketleri", labelKey: "nav.stokHareketleri", icon: ArrowLeftRight },
    { path: "/siparisler",   labelKey: "nav.siparisler",  icon: ShoppingCart },
    { path: "/faturalar",    labelKey: "nav.faturalar",   icon: Receipt },
    { path: "/raporlar",     labelKey: "nav.raporlar",    icon: BarChart3 },
    { path: "/excel-import", labelKey: "nav.excelImport", icon: FileSpreadsheet },
  ],
  muhasebe: [
    { path: "/dashboard",  labelKey: "nav.dashboard",  icon: LayoutDashboard },
    { path: "/cariler",    labelKey: "nav.cari",        icon: Users },
    { path: "/siparisler", labelKey: "nav.siparisler",  icon: ShoppingCart },
    { path: "/faturalar",  labelKey: "nav.faturalar",   icon: Receipt },
    { path: "/raporlar",   labelKey: "nav.raporlar",    icon: BarChart3 },
  ],
  depo: [
    { path: "/stoklar",           labelKey: "nav.stok",              icon: Package },
    { path: "/stok-hareketleri", labelKey: "nav.stokHareketleri", icon: ArrowLeftRight },
    { path: "/siparisler",        labelKey: "nav.siparisler",        icon: ShoppingCart },
  ],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [eurKuru, setEurKuru] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const lastOrderTotal = useRef(null);
  const audioContext = useRef(null);
  const { cartCount } = useCart();

  const playOrderSound = () => {
    if (localStorage.getItem("vagenpar_order_sound") === "off") return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      audioContext.current = audioContext.current || new AudioCtx();
      const ctx = audioContext.current;
      const volume = Number(localStorage.getItem("vagenpar_order_volume") || 0.75);
      playOrderNotification(ctx, volume);
    } catch { /* optional browser audio support */ }
  };

  const toggleLang = () => {
    const next = i18n.language === "tr" ? "en" : "tr";
    i18n.changeLanguage(next);
    localStorage.setItem("vagenpar_lang", next);
  };

  useEffect(() => {
    api.get("/settings/eur-kuru").then((r) => setEurKuru(r.data.kur)).catch(() => {});
  }, []);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/siparisler/", { params: { page: 1, per_page: 1 } });
        const newestId = data.items?.[0]?.id || "";
        const rememberedId = localStorage.getItem("vagenpar_last_order_id") || "";
        if (lastOrderTotal.current !== null && newestId && newestId !== rememberedId && data.total > lastOrderTotal.current) {
          const delta = data.total - lastOrderTotal.current;
          setNewOrderCount((count) => count + delta);
          playOrderSound();
        }
        if (newestId) localStorage.setItem("vagenpar_last_order_id", newestId);
        lastOrderTotal.current = data.total;
      } catch { /* next poll retries */ }
    };
    poll(); const timer = setInterval(poll, 20000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = NAV_ITEMS[user?.role] || NAV_ITEMS.satis_destek;
  const sidebarW = collapsed ? "w-16" : "w-64";
  const contentML = collapsed ? "ml-16" : "ml-64";

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 flex flex-col bg-[#0D1B3E] text-white ${sidebarW} shadow-2xl transition-all duration-200`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-3 border-b border-white/10 shrink-0">
          {!collapsed && (
            <div className="flex items-center flex-1 min-w-0 py-1">
              <div className="bg-white rounded-lg px-2.5 py-1.5 inline-flex items-center shadow-sm">
                <BrandLogo className="h-7 w-auto max-w-[136px]" eager />
              </div>
            </div>
          )}
          {collapsed && (
            <div className="w-8 h-8 bg-white/15 rounded-md flex items-center justify-center mx-auto shrink-0">
              <span className="text-white font-bold text-xs">VG</span>
            </div>
          )}
          <button
            data-testid={NAV.sidebarToggle}
            onClick={() => setCollapsed(!collapsed)}
            className={`text-slate-400 hover:text-white transition-colors shrink-0 ${collapsed ? "mx-auto mt-0" : "ml-auto"}`}
          >
            {collapsed ? <ChevronRight size={15} /> : <X size={15} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {navItems.map(({ path, labelKey, label, icon: Icon, cart }) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => path === "/siparisler" && setNewOrderCount(0)}
              title={collapsed ? (label || t(labelKey)) : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg mb-0.5 transition-colors duration-150 sidebar-nav-item
                ${isActive
                  ? "bg-white/15 text-white"
                  : "text-slate-400 hover:bg-white/8 hover:text-slate-200"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              <Icon size={17} className="shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{label || t(labelKey)}</span>}
              {cart && cartCount > 0 && <span className={`${collapsed ? "absolute ml-5 -mt-5" : "ml-auto"} min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center`}>{cartCount > 99 ? "99+" : cartCount}</span>}
              {path === "/siparisler" && newOrderCount > 0 && <span className={`${collapsed ? "absolute ml-5 -mt-5" : "ml-auto"} min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center`}>{newOrderCount > 99 ? "99+" : newOrderCount}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="border-t border-white/10 p-3 shrink-0">
          {!collapsed && (
            <div className="mb-2.5 px-1">
              <div className="text-sm font-medium text-white truncate">{user?.name || user?.email}</div>
              <div className="text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          )}
          <button
            data-testid={NAV.logoutButton}
            onClick={handleLogout}
            title={t("nav.logout")}
            className={`flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors w-full ${collapsed ? "justify-center" : "px-1"}`}
          >
            <LogOut size={15} />
            {!collapsed && <span className="text-sm">{t("nav.logout")}</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col min-h-screen ${contentML} transition-all duration-200`}>
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-30 px-6 flex items-center justify-between shrink-0">
          <div />
          <div className="flex items-center gap-3">
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              title={i18n.language === "tr" ? "Switch to English" : "Türkçe'ye geç"}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-md px-2.5 py-1.5 hover:bg-slate-50 hover:border-slate-300 transition-colors"
            >
              <Globe size={13} />
              {i18n.language === "tr" ? "TR" : "EN"}
            </button>
            {eurKuru && (
              <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
                <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">EUR/TRY</span>
                <span className="font-bold text-[#0D1B3E] tabular-nums">{fmt(eurKuru, 4)}</span>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-6 page-enter">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
